import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Activity, Mail, Bell, Database, Clock, CheckCircle, XCircle,
  AlertTriangle, RefreshCw, Server, HardDrive, Users, Package,
  Wrench, Ticket, FileText, Shield, MapPin, UserCheck, ShieldCheck
} from 'lucide-react';

const StatusCard = ({ icon: Icon, label, value, color = 'blue', sub }) => (
  <div className={`flex items-center gap-3 p-4 rounded-xl bg-${color}-50 border border-${color}-100`}>
    <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
      <Icon className={`w-5 h-5 text-${color}-600`} />
    </div>
    <div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SystemHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Plant/Location reconciliation state
  const [reconcile, setReconcile] = useState(null);       // last dry_run result
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileApplying, setReconcileApplying] = useState(false);

  // User access reconciliation state
  const [userAccess, setUserAccess] = useState(null);
  const [userAccessLoading, setUserAccessLoading] = useState(false);
  const [userAccessApplying, setUserAccessApplying] = useState(false);

  // Global integrity audit state
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditApplying, setAuditApplying] = useState(false);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/system/health');
      setData(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor de sanatate sistem');
    } finally {
      setLoading(false);
    }
  }, []);

  const runReconcileDryRun = useCallback(async () => {
    try {
      setReconcileLoading(true);
      const res = await api.post('/admin/reconcile-plant-locations?dry_run=true');
      setReconcile(res.data);
      const totalIssues = (res.data?.equipment_fixed || 0) + (res.data?.tickets_fixed || 0);
      if (totalIssues === 0) {
        toast.success('Nicio inconsistenta plant/locatie detectata');
      } else {
        toast.warning(`${totalIssues} inconsistente detectate (${res.data.equipment_fixed} echipamente, ${res.data.tickets_fixed} tichete)`);
      }
    } catch (error) {
      toast.error('Eroare la verificare: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReconcileLoading(false);
    }
  }, []);

  const applyReconcile = useCallback(async () => {
    const totalIssues = (reconcile?.equipment_fixed || 0) + (reconcile?.tickets_fixed || 0);
    if (totalIssues === 0) return;
    if (!window.confirm(`Esti sigur ca vrei sa repari ${totalIssues} inconsistente acum? Se vor remapa ${reconcile.equipment_fixed} echipamente si ${reconcile.tickets_fixed} tichete spre locatii cu acelasi nume in plant-ul corect.`)) {
      return;
    }
    try {
      setReconcileApplying(true);
      const res = await api.post('/admin/reconcile-plant-locations?dry_run=false');
      setReconcile(res.data);
      toast.success(`Reparat: ${res.data.equipment_fixed} echipamente si ${res.data.tickets_fixed} tichete actualizate`);
    } catch (error) {
      toast.error('Eroare la reparare: ' + (error.response?.data?.detail || error.message));
    } finally {
      setReconcileApplying(false);
    }
  }, [reconcile]);

  const runUserAccessDryRun = useCallback(async () => {
    try {
      setUserAccessLoading(true);
      const res = await api.post('/admin/reconcile-user-access?dry_run=true');
      setUserAccess(res.data);
      if ((res.data?.users_fixed || 0) === 0) {
        toast.success('Niciun utilizator cu acces orfan');
      } else {
        toast.warning(`${res.data.users_fixed} utilizatori cu referinte orfane (${res.data.orphan_plant_refs_removed} plant, ${res.data.orphan_location_refs_removed} locatii)`);
      }
    } catch (error) {
      toast.error('Eroare la verificare: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUserAccessLoading(false);
    }
  }, []);

  const applyUserAccess = useCallback(async () => {
    if ((userAccess?.users_fixed || 0) === 0) return;
    if (!window.confirm(`Esti sigur ca vrei sa curati accesul orfan pentru ${userAccess.users_fixed} utilizatori? Se vor sterge ${userAccess.orphan_plant_refs_removed} referinte plant si ${userAccess.orphan_location_refs_removed} referinte locatii din array-urile plant_access / location_access.`)) {
      return;
    }
    try {
      setUserAccessApplying(true);
      const res = await api.post('/admin/reconcile-user-access?dry_run=false');
      setUserAccess(res.data);
      toast.success(`Reparat: ${res.data.users_fixed} utilizatori actualizati`);
    } catch (error) {
      toast.error('Eroare la reparare: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUserAccessApplying(false);
    }
  }, [userAccess]);

  const runAuditDryRun = useCallback(async () => {
    try {
      setAuditLoading(true);
      const res = await api.post('/admin/integrity-audit?dry_run=true');
      setAudit(res.data);
      const issues = res.data?.total_issues || 0;
      if (issues === 0) {
        toast.success(`Audit: scor sanatate ${res.data.health_score}/100 - totul OK`);
      } else {
        toast.warning(`Audit: scor ${res.data.health_score}/100 - ${issues} probleme detectate`);
      }
    } catch (error) {
      toast.error('Eroare audit: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const applyAudit = useCallback(async () => {
    const issues = audit?.total_issues || 0;
    if (issues === 0) return;
    if (!window.confirm(`Esti sigur ca vrei sa aplici reparatiile pentru toate ${issues} probleme detectate (plant/locatie, acces utilizatori, furnizori articole)?`)) {
      return;
    }
    try {
      setAuditApplying(true);
      const res = await api.post('/admin/integrity-audit?dry_run=false');
      setAudit(res.data);
      toast.success(`Audit aplicat: scor sanatate ${res.data.health_score}/100`);
    } catch (error) {
      toast.error('Eroare audit: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAuditApplying(false);
    }
  }, [audit]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  const { email, scheduler, notifications, backups, database, recent_activity } = data;

  return (
    <div className="space-y-6" data-testid="system-health-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sanatate Sistem</h1>
          <p className="text-slate-500 text-sm mt-1">Monitorizare operationala MAINTIX</p>
        </div>
        <Button onClick={fetchHealth} variant="outline" size="sm" data-testid="refresh-health-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reincarca
        </Button>
      </div>

      {/* Overview KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="health-kpis">
        <StatusCard
          icon={notifications.critical > 0 ? AlertTriangle : CheckCircle}
          label="Notificari Active"
          value={notifications.total}
          color={notifications.critical > 0 ? 'red' : 'green'}
          sub={`${notifications.critical} critice, ${notifications.warning} avertizari`}
        />
        <StatusCard
          icon={Mail}
          label="Emailuri Trimise"
          value={email.total_sent}
          color={email.configured ? 'blue' : 'slate'}
          sub={email.configured ? `${email.total_failed} esuate` : 'SMTP neconfigurat'}
        />
        <StatusCard
          icon={Database}
          label="Backup-uri"
          value={backups.length}
          color={backups.length > 0 ? 'green' : 'amber'}
          sub={backups.length > 0 ? `Ultimul: ${new Date(backups[0]?.created_at).toLocaleDateString('ro-RO')}` : 'Niciun backup'}
        />
        <StatusCard
          icon={Users}
          label="Utilizatori Activi"
          value={database.active_users}
          color="blue"
          sub={`din ${database.total_users} total`}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Email Status */}
        <Card data-testid="email-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-600" />
              Status Email SMTP
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Configurare SMTP</span>
              <Badge variant={email.configured ? 'default' : 'destructive'} className={email.configured ? 'bg-green-600' : ''}>
                {email.configured ? 'Configurat' : 'Neconfigurat'}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Scheduler Alerte</span>
              <Badge variant={scheduler.running ? 'default' : 'secondary'} className={scheduler.running ? 'bg-green-600' : ''}>
                {scheduler.running ? 'Activ' : 'Oprit'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-xl font-bold text-green-700">{email.total_sent}</p>
                <p className="text-xs text-green-600">Trimise cu succes</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-xl font-bold text-red-700">{email.total_failed}</p>
                <p className="text-xs text-red-600">Esuate</p>
              </div>
            </div>

            {email.recent_logs.length > 0 && (
              <div className="space-y-2 mt-3">
                <p className="text-xs font-medium text-slate-500 uppercase">Ultimele emailuri</p>
                {email.recent_logs.slice(0, 3).map((log, i) => (
                  <div key={i} className="flex items-center justify-between text-sm p-2 border rounded-lg">
                    <div className="flex items-center gap-2">
                      {log.status === 'sent' ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-500" />
                      )}
                      <span className="text-slate-700 truncate max-w-[200px]">{log.subject || log.type}</span>
                    </div>
                    <span className="text-xs text-slate-400">
                      {log.sent_at ? new Date(log.sent_at).toLocaleDateString('ro-RO') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card data-testid="notifications-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-600" />
              Notificari Active
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="text-xl font-bold text-red-700">{notifications.critical}</p>
                <p className="text-xs text-red-600">Critice</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-xl font-bold text-amber-700">{notifications.warning}</p>
                <p className="text-xs text-amber-600">Avertizari</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xl font-bold text-blue-700">{notifications.total}</p>
                <p className="text-xs text-blue-600">Total</p>
              </div>
            </div>

            <div className="space-y-2 mt-2">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-red-500" />
                  <span className="text-sm">Stoc Scazut</span>
                </div>
                <Badge variant={notifications.low_stock > 0 ? 'destructive' : 'secondary'}>
                  {notifications.low_stock}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-sm">SLA Depasit/In Risc</span>
                </div>
                <Badge variant={notifications.sla > 0 ? 'destructive' : 'secondary'}>
                  {notifications.sla}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-orange-500" />
                  <span className="text-sm">Revizii Intarziate</span>
                </div>
                <Badge variant={notifications.revision > 0 ? 'destructive' : 'secondary'}>
                  {notifications.revision}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backups */}
        <Card data-testid="backups-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-green-600" />
              Backup-uri Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {backups.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Niciun backup gasit</p>
                <p className="text-xs mt-1">Accesati Backup BD pentru a crea unul</p>
              </div>
            ) : (
              <div className="space-y-2">
                {backups.map((backup, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Database className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{backup.filename}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(backup.created_at).toLocaleString('ro-RO')} | {backup.size_mb} MB
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {backup.collections} colectii
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Database Stats */}
        <Card data-testid="database-stats-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="w-4 h-4 text-indigo-600" />
              Statistici Baza de Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Ticket, label: 'Tichete', value: database.collections.tickets, color: 'blue' },
                { icon: Wrench, label: 'Revizii', value: database.collections.revisions, color: 'green' },
                { icon: Shield, label: 'Echipamente', value: database.collections.equipment, color: 'indigo' },
                { icon: Package, label: 'Articole', value: database.collections.articles, color: 'amber' },
                { icon: FileText, label: 'Stoc', value: database.collections.stock, color: 'teal' },
                { icon: Users, label: 'Furnizori', value: database.collections.suppliers, color: 'purple' },
                { icon: Wrench, label: 'Scule', value: database.collections.tools, color: 'orange' },
                { icon: Users, label: 'Utilizatori', value: database.collections.users, color: 'slate' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg">
                  <item.icon className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600 flex-1">{item.label}</span>
                  <span className="text-sm font-bold text-slate-900">{item.value || 0}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {recent_activity && recent_activity.length > 0 && (
        <Card data-testid="recent-activity-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-600" />
              Activitate Recenta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {recent_activity.slice(0, 8).map((act, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 hover:bg-slate-50 rounded-lg transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      act.action === 'create' ? 'bg-green-500' :
                      act.action === 'update' ? 'bg-blue-500' :
                      act.action === 'delete' ? 'bg-red-500' : 'bg-slate-400'
                    }`} />
                    <div>
                      <span className="text-sm text-slate-700">
                        <span className="font-medium">{act.user_name || 'Sistem'}</span>
                        {' '}{act.action === 'create' ? 'a creat' : act.action === 'update' ? 'a actualizat' : act.action === 'delete' ? 'a sters' : act.action}
                        {' '}<span className="text-slate-500">{act.entity_type}</span>
                      </span>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400">
                    {act.timestamp ? new Date(act.timestamp).toLocaleString('ro-RO', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '-'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Data Integrity Audit (global) */}
      <Card data-testid="integrity-audit-card" className="border-2 border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
            Audit Integritate Date
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Scan global combinat: coerenta plant/locatie, referinte orfane in acces utilizatori si furnizori articole. Returneaza un scor de sanatate agregat (0-100).
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={runAuditDryRun}
              disabled={auditLoading || auditApplying}
              className="bg-indigo-600 hover:bg-indigo-700"
              size="sm"
              data-testid="integrity-audit-dry-run-btn"
            >
              {auditLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Ruleaza audit complet
            </Button>
            {audit && (audit.total_issues || 0) > 0 && audit.dry_run && (
              <Button
                onClick={applyAudit}
                disabled={auditApplying || auditLoading}
                variant="outline"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                size="sm"
                data-testid="integrity-audit-apply-btn"
              >
                {auditApplying ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Repara tot ({audit.total_issues})
              </Button>
            )}
          </div>

          {audit && (
            <div className="space-y-4" data-testid="integrity-audit-results">
              {/* Health score bar */}
              <div className="flex items-center gap-4 p-4 rounded-xl border bg-gradient-to-br from-slate-50 to-white">
                <div className="relative w-20 h-20 flex-shrink-0">
                  <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                    <path className="text-slate-200" stroke="currentColor" strokeWidth="3" fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path
                      className={audit.health_score >= 90 ? 'text-green-500' : audit.health_score >= 70 ? 'text-amber-500' : 'text-red-500'}
                      stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round"
                      strokeDasharray={`${audit.health_score}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-xl font-bold text-slate-900">{audit.health_score}</span>
                    <span className="text-[10px] text-slate-500 uppercase">/ 100</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">
                    {audit.health_score >= 90 ? 'Integritate excelenta' :
                     audit.health_score >= 70 ? 'Integritate moderata' :
                     'Integritate compromisa'}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {audit.total_issues > 0
                      ? `${audit.total_issues} probleme detectate (${audit.total_unfixable} nereparabile automat)`
                      : 'Toate verificarile au trecut fara probleme.'}
                  </p>
                </div>
              </div>

              {/* Per-check status */}
              <div className="grid gap-2">
                {audit.checks.map((c) => (
                  <div key={c.key} className={`flex items-center gap-3 p-3 rounded-lg border ${
                    c.status === 'ok' ? 'bg-green-50 border-green-100' :
                    c.status === 'warn' ? 'bg-amber-50 border-amber-100' :
                    'bg-red-50 border-red-100'
                  }`}>
                    {c.status === 'ok' ?
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" /> :
                      <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${c.status === 'warn' ? 'text-amber-600' : 'text-red-600'}`} />
                    }
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{c.label}</p>
                      <p className="text-xs text-slate-500">
                        {c.issues > 0
                          ? `${c.issues} probleme ${c.unfixable > 0 ? `(${c.unfixable} nereparabile)` : ''} din ${c.scanned} scanate`
                          : `${c.scanned} inregistrari verificate, toate OK`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plant/Location Reconciliation */}
      <Card data-testid="plant-location-reconcile-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-purple-600" />
            Coerenta Plant / Locatie
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Detecteaza si repara echipamentele si tichetele unde <strong>plant_id</strong> nu se potriveste cu plant-ul locatiei
            asociate. Utilitarul incearca sa remape fiecare inregistrare la o locatie cu acelasi nume in plant-ul corect.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={runReconcileDryRun}
              disabled={reconcileLoading || reconcileApplying}
              variant="outline"
              size="sm"
              data-testid="reconcile-dry-run-btn"
            >
              {reconcileLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Verifica coerenta plant/locatie
            </Button>
            {reconcile && ((reconcile.equipment_fixed || 0) + (reconcile.tickets_fixed || 0)) > 0 && reconcile.dry_run && (
              <Button
                onClick={applyReconcile}
                disabled={reconcileApplying || reconcileLoading}
                className="bg-purple-600 hover:bg-purple-700"
                size="sm"
                data-testid="reconcile-apply-btn"
              >
                {reconcileApplying ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Repara acum ({(reconcile.equipment_fixed || 0) + (reconcile.tickets_fixed || 0)})
              </Button>
            )}
          </div>

          {reconcile && (
            <div className="space-y-3" data-testid="reconcile-results">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-lg font-bold text-slate-900">{reconcile.equipment_scanned}</p>
                  <p className="text-xs text-slate-500">Echipamente scanate</p>
                </div>
                <div className={`text-center p-3 rounded-lg border ${reconcile.equipment_fixed > 0 ? 'bg-amber-50 border-amber-100' : 'bg-green-50 border-green-100'}`}>
                  <p className={`text-lg font-bold ${reconcile.equipment_fixed > 0 ? 'text-amber-700' : 'text-green-700'}`}>{reconcile.equipment_fixed}</p>
                  <p className="text-xs text-slate-500">{reconcile.dry_run ? 'Echipamente de reparat' : 'Echipamente reparate'}</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-lg font-bold text-slate-900">{reconcile.tickets_scanned}</p>
                  <p className="text-xs text-slate-500">Tichete scanate</p>
                </div>
                <div className={`text-center p-3 rounded-lg border ${reconcile.tickets_fixed > 0 ? 'bg-amber-50 border-amber-100' : 'bg-green-50 border-green-100'}`}>
                  <p className={`text-lg font-bold ${reconcile.tickets_fixed > 0 ? 'text-amber-700' : 'text-green-700'}`}>{reconcile.tickets_fixed}</p>
                  <p className="text-xs text-slate-500">{reconcile.dry_run ? 'Tichete de reparat' : 'Tichete reparate'}</p>
                </div>
              </div>

              {(reconcile.equipment_unfixable > 0 || reconcile.tickets_unfixable > 0) && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Inconsistente care nu pot fi reparate automat: {reconcile.equipment_unfixable} echipamente, {reconcile.tickets_unfixable} tichete</p>
                    <p className="text-xs mt-1">Nu exista o locatie cu acelasi nume in plant-ul corect. Trebuie intervenit manual (creeaza locatia sau reasignaza echipamentul).</p>
                  </div>
                </div>
              )}

              {reconcile.samples?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b text-xs font-medium text-slate-600 uppercase">
                    Exemple {reconcile.dry_run ? 'ce vor fi modificate' : 'modificate'} (max 10)
                  </div>
                  <div className="divide-y">
                    {reconcile.samples.map((s, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="text-[10px] uppercase">{s.kind}</Badge>
                          <span className="text-slate-700 truncate">
                            {s.code ? <code className="bg-slate-100 px-1 rounded">{s.code}</code> : null}
                            {' '}{s.name || s.title || s.id?.slice(0, 8)}
                          </span>
                        </div>
                        <span className="text-slate-500 font-mono whitespace-nowrap">
                          {s.from_location_id?.slice(0, 8)} → {s.to_location_id?.slice(0, 8)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reconcile.unfixable_samples?.length > 0 && (
                <div className="border border-red-100 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-red-50 border-b border-red-100 text-xs font-medium text-red-700 uppercase">
                    Inconsistente nereparabile (max 10)
                  </div>
                  <div className="divide-y">
                    {reconcile.unfixable_samples.map((s, i) => (
                      <div key={i} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
                        <span className="text-slate-700 truncate">
                          {s.code && <code className="bg-slate-100 px-1 rounded">{s.code}</code>}
                          {' '}plant={s.plant_name || s.plant_id?.slice(0, 8)} / location=&quot;{s.location_name || '-'}&quot;
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Access Reconciliation */}
      <Card data-testid="user-access-reconcile-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-emerald-600" />
            Acces Utilizatori (plant / locatii orfane)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Detecteaza si elimina din array-urile <strong>plant_access</strong> / <strong>location_access</strong> ale utilizatorilor
            referintele catre plant-uri sau locatii care nu mai exista in sistem.
            Previne portaluri corupte pentru sefi de sectie dupa stergerea unor sedii/locatii.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={runUserAccessDryRun}
              disabled={userAccessLoading || userAccessApplying}
              variant="outline"
              size="sm"
              data-testid="user-access-dry-run-btn"
            >
              {userAccessLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Verifica acces utilizatori
            </Button>
            {userAccess && (userAccess.users_fixed || 0) > 0 && userAccess.dry_run && (
              <Button
                onClick={applyUserAccess}
                disabled={userAccessApplying || userAccessLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                size="sm"
                data-testid="user-access-apply-btn"
              >
                {userAccessApplying ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Curata acum ({userAccess.users_fixed})
              </Button>
            )}
          </div>

          {userAccess && (
            <div className="space-y-3" data-testid="user-access-results">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-lg font-bold text-slate-900">{userAccess.users_scanned}</p>
                  <p className="text-xs text-slate-500">Utilizatori scanati</p>
                </div>
                <div className={`text-center p-3 rounded-lg border ${userAccess.users_fixed > 0 ? 'bg-amber-50 border-amber-100' : 'bg-green-50 border-green-100'}`}>
                  <p className={`text-lg font-bold ${userAccess.users_fixed > 0 ? 'text-amber-700' : 'text-green-700'}`}>{userAccess.users_fixed}</p>
                  <p className="text-xs text-slate-500">{userAccess.dry_run ? 'De curatat' : 'Curatati'}</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-lg font-bold text-slate-900">{userAccess.orphan_plant_refs_removed}</p>
                  <p className="text-xs text-slate-500">{userAccess.dry_run ? 'Plant refs de sters' : 'Plant refs sterse'}</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-lg font-bold text-slate-900">{userAccess.orphan_location_refs_removed}</p>
                  <p className="text-xs text-slate-500">{userAccess.dry_run ? 'Location refs de sters' : 'Location refs sterse'}</p>
                </div>
              </div>

              {userAccess.samples?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b text-xs font-medium text-slate-600 uppercase">
                    Utilizatori afectati (max 10)
                  </div>
                  <div className="divide-y">
                    {userAccess.samples.map((s, i) => (
                      <div key={i} className="px-3 py-2 text-xs">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="text-[10px] uppercase">{s.role || '-'}</Badge>
                            <span className="text-slate-700 font-medium truncate">{s.full_name || s.email}</span>
                            <span className="text-slate-400 truncate">&lt;{s.email}&gt;</span>
                          </div>
                        </div>
                        <div className="text-slate-500 pl-1 space-y-0.5">
                          {s.removed_plant_ids?.length > 0 && (
                            <div>
                              <span className="text-[10px] uppercase text-red-600 mr-1">Plant orphans:</span>
                              <span className="font-mono">{s.removed_plant_ids.map(id => id?.slice(0, 8)).join(', ')}</span>
                            </div>
                          )}
                          {s.removed_location_ids?.length > 0 && (
                            <div>
                              <span className="text-[10px] uppercase text-red-600 mr-1">Location orphans:</span>
                              <span className="font-mono">{s.removed_location_ids.map(id => id?.slice(0, 8)).join(', ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemHealthPage;

